import _ from 'lodash'
import { EntityTable, IDType } from 'dexie'

import { DataSynchronisationStatus, DataUpdateCacherStats } from '@skinsoft/types'
import { dbCacheUp } from './dexie/cache-up'

const LAST_PUSH_DATE_CACHE_KEY = 'last-push-date';
export class OfflineDataUpdateCacher<T extends { id: string }> {
    protected table: EntityTable<Partial<T>, 'id'>

    constructor(table: EntityTable<Partial<T>, 'id'>) {
        this.table = table
    }

    // methods to manage push stats
    public static async setLastPushStats(date: number, status: DataSynchronisationStatus) {
        await dbCacheUp.syncUpStats.put(
            {
                date,
                status,
            },
            LAST_PUSH_DATE_CACHE_KEY
        )
    }

    public static async getLastPushStats(): Promise<DataUpdateCacherStats | undefined> {
        return await dbCacheUp.syncUpStats.get(LAST_PUSH_DATE_CACHE_KEY)
    }

    public static async isPushError(): Promise<boolean> {
        const stats = await this.getLastPushStats();
        if (stats) {
            return stats.status === DataSynchronisationStatus.ERROR ||
                stats.status === DataSynchronisationStatus.ABORTED
        }
        return false;
    }

    public static async clear() {
        await dbCacheUp.tables.forEach(async table => {
            if(table.name !== 'syncUpStats') {
                await table.clear()
            }
        })
    }

    public static isTemporaryId(id: string): boolean {
        return id?.length > 0 && 
            (typeof id === 'string' || (id as any) instanceof String) && 
            id.startsWith('_')
    }

    public static getAllEntities(): Promise<Array<object>> {
        const promises: Promise<Array<object>>[] = []
        dbCacheUp.tables.forEach(table => {
            promises.push(table.toArray())
        })

        return Promise.all(promises)
    }

    public storeBeforePushing(id: string, body: Partial<T> & {id: string}): Promise<string | null> {
        if (body.id) {
            Object.defineProperty(body, "fromUpdateCache", {
                enumerable: true,
                value: true,
            })
            return this.table.put(body) as Promise<string>
        }
        return Promise.resolve(null)
    }

    public deleteBeforePushing(id: string) {
        if (id) {
            this.table.delete(id as IDType<Partial<T>, "id">)
        }
    }

    public getCacheKey(id: string): string {
        return id
    }

    public getSanitizedId(id: string) {
        return id.substring(id.lastIndexOf('-') + 1);
    }

    public getCachedValue(id: string): Promise<Partial<T> | undefined> {
        return this.table.get(id as IDType<Partial<T>, "id">)
    }

    public removeCachedValue(id: string) {
        return this.table.delete(id as IDType<Partial<T>, "id">)
    }

    public async renameCacheKey(id: string, newId: string) {
        const value: Partial<T> | undefined = await this.getCachedValue(id)
        if(value) {
            value.id = newId
            await this.storeBeforePushing(newId, value as Partial<T> & { id: string })
        } else {
            await this.storeBeforePushing(newId, { id: newId } as Partial<T> & { id: string })
        }
        await this.removeCachedValue(id)
    }

    public async getCachedObjectsKeys(countLocallyCreated: boolean = true, keepPrefix: boolean = true): Promise<string[]> {
        return this.table.toCollection().primaryKeys() as Promise<string[]>
    }

    /**
     * Returns all objects that were locally created in offline mode, 
     * and not pushed to the db yet
     */
    public async getLocallyCreatedCachedObjects(): Promise<Partial<T>[]> {
        return this.getObjectsWithPredicate(id => id.includes('_'))
    }

    /**
     * Returns all objects that were locally modified in offline mode, 
     * and not pushed to the db yet
     */
    public async getLocallyUpdatedCachedObjects() {
        return await this.getObjectsWithPredicate(id => !id.includes('_'))
    }

    public async getAllObjects(): Promise<Partial<T>[]> {
        return this.table.toArray()
    }

    /**
     * get all cached entity that are really entities (exludes stats and undefined)
     */
    public async getAllRealObjects(includeCashedCreations: boolean = true): Promise<Partial<T>[]> {
        return this.getAllObjects()
    }

    public async getNbCachedObjects(countLocallyCreated: boolean = true): Promise<number> {
        return await this.table.count()
    }

    public async getNbCachedCreations() {
        return (await this.getCachedObjectsKeys(true)).filter(key => {
            return OfflineDataUpdateCacher.isTemporaryId(
                this.getSanitizedId(key)
            )
        }).length
    }

    public async fetchOneFromId(idToMatch: string): Promise<Partial<T> | undefined> {
        return this.getCachedValue(idToMatch)
    }

    /**
     * Returns the objects in cache matching a predicate
     * @param predicate 
     */
    private async getObjectsWithPredicate(predicate: (id: string) => boolean): Promise<Partial<T>[]> {
        return this.table.filter(value => {
            if(!value?.id) {
                return false
            }
            return predicate(value.id)
        }).toArray()
    }
}